import React from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { PaziaButton, PaziaCheckbox, PaziaInput } from "components/atoms";
import { PaziaModal } from "components/molecules";
import { ModalHeader } from "components/templates/Modals/ModalHeader";
import { ModalPadding } from "components/templates/Modals/ModalPadding";
import { studentFromsApi, UpdateBuyerFieldsRequest } from "api/studentFromsApi";
import { useToaster } from "utils";

interface EditRegistrationBuyerFieldsModalProps {
  studentId: number;
  courseId: number;
  initialValues: {
    isInvoice: boolean;
    buyer?: string;
    buyerNIP?: string;
    buyerAddress?: string;
  };
  onClose: () => void;
  onSuccess: () => void;
}

interface FormValues {
  isInvoice: boolean;
  buyer: string;
  buyerNIP: string;
  buyerAddress: string;
}

const schema: yup.ObjectSchema<FormValues> = yup.object({
  isInvoice: yup.boolean().required().default(false),
  buyer: yup
    .string()
    .when("isInvoice", {
      is: true,
      then: (s) => s.required("Wymagane"),
      otherwise: (s) => s.optional(),
    })
    .default(""),
  buyerNIP: yup
    .string()
    .when("isInvoice", {
      is: true,
      then: (s) => s.required("Wymagane"),
      otherwise: (s) => s.optional(),
    })
    .default(""),
  buyerAddress: yup
    .string()
    .when("isInvoice", {
      is: true,
      then: (s) => s.required("Wymagane"),
      otherwise: (s) => s.optional(),
    })
    .default(""),
}) as yup.ObjectSchema<FormValues>;

export const EditRegistrationBuyerFieldsModal = ({
  studentId,
  courseId,
  initialValues,
  onClose,
  onSuccess,
}: EditRegistrationBuyerFieldsModalProps) => {
  const { showError, showSuccess } = useToaster();

  const methods = useForm<FormValues>({
    defaultValues: {
      isInvoice: initialValues.isInvoice,
      buyer: initialValues.buyer ?? "",
      buyerNIP: initialValues.buyerNIP ?? "",
      buyerAddress: initialValues.buyerAddress ?? "",
    },
    resolver: yupResolver(schema),
  });

  const isInvoice = useWatch({ control: methods.control, name: "isInvoice" });

  const onSubmit = async (values: FormValues) => {
    try {
      const body: UpdateBuyerFieldsRequest = {
        isInvoice: values.isInvoice,
        buyer: values.isInvoice ? values.buyer : undefined,
        buyerNip: values.isInvoice ? values.buyerNIP : undefined,
        buyerAddress: values.isInvoice ? values.buyerAddress : undefined,
      };
      await studentFromsApi.updateBuyerFields(studentId, courseId, body);
      showSuccess("Dane nabywcy zostały zaktualizowane");
      onSuccess();
    } catch {
      showError("Wystąpił błąd podczas zapisywania danych nabywcy");
    }
  };

  return (
    <PaziaModal id="edit-registration-buyer-fields-modal" onCloseRequest={onClose} isOpen size="2xl">
      <ModalPadding>
        <ModalHeader>Edytuj dane nabywcy (rejestracja)</ModalHeader>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} className="space-y-4 mt-4">
            <PaziaCheckbox name="isInvoice" label="Chcę fakturę VAT" />
            {isInvoice && (
              <>
                <PaziaInput name="buyer" label="Nabywca" required />
                <PaziaInput name="buyerNIP" label="NIP" required />
                <PaziaInput name="buyerAddress" label="Adres nabywcy" required />
              </>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <PaziaButton btnType="button" title="Anuluj" type="secondary" onClick={onClose} />
              <PaziaButton btnType="submit" title="Zapisz" type="primary" />
            </div>
          </form>
        </FormProvider>
      </ModalPadding>
    </PaziaModal>
  );
};
